import { formatCurrency, formatDate, plural, titleCase, type StatusLevel } from '@westhetics/ui'
import { isoToBrDate } from '@/lib/dates'
import type {
  EmailStatus,
  ImageConsent,
  LotSlot,
  ProductUnit,
  Stage,
  TreatmentPlan,
  TreatmentPlanFilter,
  TreatmentPlanStatus,
} from './api'

/**
 * As frases da área de planos (docs/areas/planos-de-tratamento.md; docs/styleguide/04-escrita.md):
 * filtros, etapas, slots do lote, status por extenso, os vazios e os avisos. O rótulo de um valor
 * de enum sai daqui, nunca do valor cru.
 */

export const FILTERS: readonly TreatmentPlanFilter[] = [
  'open',
  'closed',
  'all',
  'pending',
  'archived',
]

export const FILTER_LABELS: Record<TreatmentPlanFilter, string> = {
  open: 'Ativos',
  closed: 'Finalizados',
  all: 'Todos',
  pending: 'Só com pendência',
  archived: 'Arquivados',
}

export const STAGES: readonly Stage[] = ['marking', 'before', 'immediately_after', 'after']

export const STAGE_LABELS: Record<Stage, { title: string; description: string }> = {
  marking: { title: 'Marcação', description: 'Antes de aplicar, com a marcação feita' },
  before: { title: 'Antes', description: 'Paciente antes do tratamento' },
  immediately_after: { title: 'Pós-imediato', description: 'Logo após o procedimento' },
  after: { title: 'Depois', description: 'No retorno, com o resultado' },
}

export const SLOTS: readonly LotSlot[] = ['vial', 'box', 'product_label']

export const SLOT_LABELS: Record<LotSlot, string> = {
  vial: 'Frasco',
  box: 'Caixa',
  product_label: 'Etiqueta',
}

export const UNITS: readonly ProductUnit[] = ['ml', 'U', 'mg', 'unit', 'session']

export const UNIT_LABELS: Record<ProductUnit, string> = {
  ml: 'ml',
  U: 'U',
  mg: 'mg',
  unit: 'unidade',
  session: 'sessão',
}

export const STATUS_LABELS: Record<TreatmentPlanStatus, string> = {
  open: 'Em aberto',
  closed: 'Encerrado',
  archived: 'Arquivado',
}

/** Em aberto em contorno ink; Encerrado preenchido em ink (a classe); Arquivado em cinza. */
export const STATUS_LEVELS: Record<TreatmentPlanStatus, StatusLevel> = {
  open: 'done',
  closed: 'done',
  archived: 'waiting',
}

export const IMAGE_CONSENT_LABELS: Record<ImageConsent, string> = {
  yes: 'Autoriza',
  no: 'Não autoriza',
  not_provided: 'Não informado',
}

export const EMAIL_STATUS_LABELS: Record<EmailStatus, string> = {
  queued: 'na fila',
  sent: 'enviado',
  delivered: 'entregue',
  read: 'aberto',
  failed: 'não entregue',
  not_sent: 'não enviado',
}

export const EMAIL_RECIPIENT_LABELS = { patient: 'e-mail à paciente', clinic: 'e-mail à clínica' }

export const CRITERION =
  'O que ainda pede foto, produto ou encerramento aparece primeiro; depois, os mais recentes.'

export const FOOTER_NOTE =
  'A coluna de pendência é a razão de existir desta tela: ela mostra onde falta prova.'

export const ORDER_NOTES: Record<TreatmentPlanFilter, string> = {
  open: 'ordem: com pendência primeiro, depois os criados mais recentemente',
  all: 'ordem: com pendência primeiro, depois os criados mais recentemente',
  pending: 'ordem: os criados mais recentemente primeiro',
  closed: 'ordem: os encerrados mais recentemente primeiro',
  archived: 'ordem: os arquivados mais recentemente primeiro',
}

export const MSG_CREATED = 'Plano criado — registre o primeiro produto'
export const MSG_PRODUCT_ADDED = 'Produto adicionado — falta fotografar o lote'
export const MSG_CLOSED = 'Plano encerrado — segue disponível como prova'
export const MSG_LABEL_READING_UNAVAILABLE = 'A leitura da etiqueta não está disponível agora.'
export const MSG_PDF_FAILED =
  'Não conseguimos gerar o PDF; o plano continua em aberto. Tente de novo em instantes.'

export const PRODUCT_FOOTER =
  'Cada produto aplicado precisa de lote fotografado — é a rastreabilidade exigida em caso de intercorrência.'
export const PHOTOS_FOOTER =
  'Fotografar pelo celular: leia o QR e as imagens entram nesta etapa em tempo real. O código vale 1 hora.'
export const QR_VALIDITY = 'O código vale 1 hora.'
export const QR_LINK_NOTE = 'O link dá acesso a enviar fotos para esta etapa durante 1 hora.'
export const REGIONS_HINT = 'Escolha pelo mapa de face ou pela lista.'
export const CONTINUE_ON_DESKTOP = 'continuar no computador'

export const EMPTY_PENDING = {
  title: 'Nenhum plano com pendência',
  description: 'Todos os planos ativos têm produto e fotos registrados.',
}

export const EMPTY_PATIENT = {
  title: 'Nenhum plano de tratamento ainda',
  description:
    'O plano registra produto, lote, região aplicada e fotos de cada etapa — é o que sustenta a defesa da clínica se o resultado for questionado.',
}

export const EMPTY_LIST = {
  title: 'Nenhum plano ainda',
  description:
    'O plano nasce quando o contrato de prestação é gerado, ou à mão em "+ Novo plano". Produto, lote, região e fotos de cada etapa ficam registrados nele.',
}

/** "32 planos · o que ainda pede foto, produto ou encerramento aparece primeiro." */
export function subtitle(total: number | undefined) {
  if (total === undefined) return 'Onde falta prova: produto, lote fotografado e fotos por etapa.'
  return `${plural(total, 'plano')} · o que ainda pede foto, produto ou encerramento aparece primeiro.`
}

/** "Bigode chinês e mento": os procedimentos em uma frase, os seguintes em minúscula. */
export function joinProcedures(names: readonly string[]): string {
  const items = names.map((name, index) =>
    index === 0 ? name : name.charAt(0).toLocaleLowerCase('pt-BR') + name.slice(1),
  )
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]!}`
}

/** "Plano de 05/09/2026 · Bigode chinês e mento · Camila R. de Castro · R$ 6.000". */
export function planSummary(plan: TreatmentPlan): string {
  const date = plan.procedureDate
    ? isoToBrDate(plan.procedureDate)
    : formatDate(new Date(plan.createdAt))
  return [
    `Plano de ${date}`,
    joinProcedures(plan.procedures.map((p) => p.name)),
    titleCase(plan.practitioner.name),
    formatCurrency(plan.totalAmountCents / 100, { compact: true }),
  ]
    .filter(Boolean)
    .join(' · ')
}

/** "Plano de 05/09" — o rótulo curto de um plano numa lista. */
export function planShortLabel(createdAt: string, procedureDate?: string | null) {
  const date = procedureDate ? new Date(`${procedureDate}T12:00:00`) : new Date(createdAt)
  return `plano de ${formatDate(date, { year: false })}`
}

/** "0 de 4 etapas registradas". */
export function recordedStagesText(count: number) {
  return `${count} de 4 etapas registradas`
}

/** "1 de 4" para a coluna ETAPAS; `pending` diz se vai em plum. */
export function stagesCell(count: number, status: TreatmentPlanStatus) {
  return { text: `${count} de 4`, pending: status === 'open' && count < 4 }
}

/** "nenhuma foto" · "1 foto" · "2 fotos". */
export function photoCount(count: number) {
  return count === 0 ? 'nenhuma foto' : plural(count, 'foto')
}

/** As etapas sem foto, por nome: ["pós-imediato", "depois"]. */
export function stagesWithoutPhoto(plan: Pick<TreatmentPlan, 'photos'>): string[] {
  return STAGES.filter((stage) => (plan.photos[stage] ?? []).length === 0).map((stage) =>
    STAGE_LABELS[stage].title.toLocaleLowerCase('pt-BR'),
  )
}

/** A frase das etapas que faltam no modal de encerrar (RN-PLA-26). */
export function missingStagesSentence(missing: string[]): string | null {
  if (missing.length === 0) return null
  const list =
    missing.length === 1
      ? missing[0]!
      : `${missing.slice(0, -1).join(', ')} e ${missing[missing.length - 1]!}`
  return `${missing.length === 1 ? 'Falta foto em 1 etapa' : `Faltam fotos em ${missing.length} etapas`}: ${list}.`
}

/** O efeito do encerramento, com quem recebe a cópia (RN-PLA-26). */
export function closeEffect(plan: TreatmentPlan, patientEmail: string | null | undefined) {
  const copy = patientEmail
    ? `A paciente recebe a cópia por e-mail (${patientEmail}) e a clínica também.`
    : 'A clínica recebe a cópia por e-mail; a paciente não tem e-mail no cadastro.'
  return `O plano vira registro de prova: ganha protocolo e hash, gera o PDF e não aceita mais alterações. ${copy}`
}

/** "Produto 2" — o título do cartão. */
export function productTitle(position: number) {
  return `Produto ${position}`
}

/** O nome do produto para frases: o digitado, ou o título do cartão. */
export function productName(product: { name: string | null; position: number }) {
  return product.name?.trim() || productTitle(product.position)
}

/** "1,0" — a medida como a pessoa digita. */
export function quantityText(quantity: number | null) {
  if (quantity === null) return ''
  return String(quantity).replace('.', ',')
}

/** "1,0" → 1; `null` quando não é número. */
export function parseQuantity(text: string): number | null {
  const normalized = text.trim().replace(',', '.')
  if (!/^\d+(\.\d{1,3})?$/.test(normalized)) return null
  const value = Number(normalized)
  return value > 0 ? value : null
}

/** "HH:mm" válido. */
export function isValidTime(text: string) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(text.trim())
}

/** "14:20 · Camila" — a legenda de uma foto. */
export function photoCaption(photo: {
  takenAt: string | null
  uploadedAt: string | null
  uploadedBy: { name: string } | null
}) {
  const at = photo.takenAt ?? photo.uploadedAt
  const time = at
    ? new Date(at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : null
  const who = photo.uploadedBy ? titleCase(photo.uploadedBy.name).split(' ')[0] : null
  return [time, who].filter(Boolean).join(' · ')
}

/** "Arquivado em 12/09 por Camila · motivo". */
export function archivedLine(row: {
  archivedAt: string | null
  archivedBy?: { name: string } | null
  archiveReason?: string | null
}) {
  const parts = [
    row.archivedAt
      ? `Arquivado em ${formatDate(new Date(row.archivedAt), { year: false })}`
      : 'Arquivado',
    row.archivedBy ? `por ${titleCase(row.archivedBy.name).split(' ')[0]}` : null,
  ]
  const head = parts.filter(Boolean).join(' ')
  return row.archiveReason ? `${head} · ${row.archiveReason}` : head
}
